import { useCallback, useEffect, useState } from "react";
import { Plus, BellRing, Eye, Lock, FilePlus2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canWrite } from "@/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { FieldInput, useRefOptions, invalidateRef } from "@/components/FieldInput";
import { FormFields, compact, FORM_DIALOG } from "@/components/RecordDialog";
import { DetailGrid } from "@/components/PageHeader";

const SITES = ["My Home Avali", "My Home Akrida", "Tellapur Technocity"];
const PAY_TERMS = ["Advance", "15 days", "30 days", "45 days", "60 days", "Against delivery", "Milestone based"];

const RFQ_FIELDS = [
  { section: "Requirement", name: "material", label: "Material", type: "ref", ref: "materials", required: true },
  { section: "Requirement", name: "quantity", label: "Quantity (with unit)" },
  { section: "Requirement", name: "delivery_location", label: "Delivery Location", type: "select", options: SITES },
  { section: "Requirement", name: "specification", label: "Specification / Grade / Make", type: "textarea" },
  { section: "Schedule & terms", name: "delivery_date", label: "Delivery By", type: "date" },
  { section: "Schedule & terms", name: "due_date", label: "Quote Due Date", type: "date", required: true },
  { section: "Schedule & terms", name: "payment_terms", label: "Payment Terms", type: "select", options: PAY_TERMS },
  { section: "Schedule & terms", name: "contact_person", label: "Purchase Contact Person" },
  { section: "Schedule & terms", name: "terms", label: "Other Terms & Conditions", type: "textarea" },
];

const CreatePODialog = ({ rfq, onClose, onDone }) => {
  const [f, setF] = useState({ quantity: parseFloat(rfq.quantity) || "" });
  const save = async () => {
    try {
      const { data } = await api.post(`/rfqs/${rfq.id}/create-po`, { site: f.site, quantity: Number(f.quantity) });
      invalidateRef("purchase_orders");
      toast.success(`${data.po_code} created for ${data.vendor} (L1)`);
      onDone(data.rfq);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-create-po">
        <DialogHeader><DialogTitle className="font-heading">Create PO from L1 quote · {rfq.rfq_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">A draft Purchase Order is created for the lowest quote; rate = quote ÷ quantity. The RFQ is closed.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Site</Label><FieldInput field={{ name: "po_site", type: "select", options: SITES }} value={f.site} onChange={(v) => setF((s) => ({ ...s, site: v }))} /></div>
          <div className="space-y-1.5"><Label>Quantity (numeric)</Label><FieldInput field={{ name: "po_quantity", type: "number" }} value={f.quantity} onChange={(v) => setF((s) => ({ ...s, quantity: v }))} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="btn-confirm-create-po">Create PO</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const deliverySummary = (vs) => {
  const bounced = vs.filter((v) => v.status === "Bounced").length;
  const unread = vs.filter((v) => ["Delivered", "Pending"].includes(v.status)).length;
  if (bounced) return `${bounced} bounced`;
  if (unread) return `${unread} unread`;
  return "All read";
};

const NewRFQDialog = ({ open, onOpenChange, onCreated }) => {
  const vendors = useRefOptions("vendors");
  const [f, setF] = useState({ vendor_ids: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ vendor_ids: [] }); }, [open]);
  const toggle = (id) => setF((s) => ({ ...s, vendor_ids: s.vendor_ids.includes(id) ? s.vendor_ids.filter((x) => x !== id) : [...s.vendor_ids, id] }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/rfqs", { ...compact(f), vendor_ids: f.vendor_ids });
      toast.success(`${data.rfq.rfq_no} sent · ${data.sent} email(s) delivered${data.failed ? `, ${data.failed} failed` : ""}`);
      onCreated(data.rfq);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-new-rfq" className={FORM_DIALOG}>
        <DialogHeader><DialogTitle className="font-heading">New RFQ</DialogTitle></DialogHeader>
        <FormFields fields={RFQ_FIELDS} form={f} setForm={setF} />
        <div className="space-y-2">
          <Label>Send to vendors ({f.vendor_ids.length})</Label>
          <div className="grid max-h-40 grid-cols-1 gap-x-3 overflow-y-auto rounded border border-slate-200 p-2 sm:grid-cols-2">
            {vendors.filter((v) => v.status === "Active").map((v) => (
              <label key={v.id} className="flex items-center gap-2 rounded px-2 py-0.5 text-xs hover:bg-slate-50">
                <Checkbox checked={f.vendor_ids.includes(v.id)} onCheckedChange={() => toggle(v.id)} data-testid={`check-vendor-${v.code}`} />
                <span className="flex-1">{v.name}</span><span className="text-xs text-slate-400">{v.email}</span>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} data-testid="btn-send-rfq">{busy ? "Sending..." : "Create & email vendors"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const VendorRow = ({ v, rfq, editable, onUpdated, l1 }) => {
  const [quote, setQuote] = useState("");
  const upd = async (body) => {
    try { onUpdated((await api.put(`/rfqs/${rfq.id}/vendors/${v.vendor_id}`, body)).data); setQuote(""); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const meta = v.status === "Read" || v.status === "Quoted" ? `Read ${fmtDate(v.read_at)}` : v.status;
  return (
    <li className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center" data-testid={`vendor-status-${v.vendor_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{v.name}{l1 && <span className="ml-2 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] text-white">L1</span>}</p>
        <p className="text-xs text-slate-500">{meta}{v.quote_amount ? ` · Quote ${inr(v.quote_amount)}` : ""}</p>
      </div>
      <StatusBadge value={v.status} />
      {editable && !v.quote_received && (
        <div className="flex items-center gap-1">
          {v.status !== "Read" && <Button size="sm" variant="ghost" onClick={() => upd({ status: "Read" })} data-testid={`btn-mark-read-${v.vendor_id}`}><Eye className="mr-1 h-3.5 w-3.5" />Read</Button>}
          <Input className="h-8 w-28 font-mono text-xs" type="number" placeholder="Quote ₹" value={quote} onChange={(e) => setQuote(e.target.value)} data-testid={`input-quote-${v.vendor_id}`} />
          <Button size="sm" variant="outline" disabled={!quote} onClick={() => upd({ quote_amount: Number(quote) })} data-testid={`btn-record-quote-${v.vendor_id}`}>Save</Button>
        </div>
      )}
    </li>
  );
};

export default function RFQTracker() {
  const { user } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [sel, setSel] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [poFor, setPoFor] = useState(null);
  const load = useCallback(() => api.get("/rfqs").then((r) => { setRfqs(r.data); setSel((s) => s || r.data[0]?.id); }), []);
  useEffect(() => { load(); }, [load]);
  const rfq = rfqs.find((r) => r.id === sel);
  const editable = canWrite(user, "purchase") && rfq?.status === "Open";
  const replace = (x) => setRfqs((a) => a.map((y) => (y.id === x.id ? x : y)));
  const quotes = rfq?.vendors.filter((v) => v.quote_received).map((v) => v.quote_amount) || [];
  const l1 = quotes.length ? Math.min(...quotes) : null;

  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rfqs/${rfq.id}/remind`);
      replace(data.rfq);
      toast.success(`Reminder sent to ${data.sent} vendor(s)${data.failed ? `, ${data.failed} failed` : ""}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = async () => {
    try { replace((await api.post(`/rfqs/${rfq.id}/close`)).data); toast.success("RFQ closed"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader dept="Purchase" title="RFQ & Vendor Response Tracker" subtitle="Email RFQs to vendors, track delivery / read status and collect quotes">
        {canWrite(user, "purchase") && <Button onClick={() => setNewOpen(true)} data-testid="btn-new-rfq"><Plus className="mr-1.5 h-4 w-4" />New RFQ</Button>}
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-12">
        <Panel className="min-w-0 lg:col-span-7" title={`All RFQs · ${rfqs.length} (${rfqs.filter((r) => r.status === "Open").length} open)`} testId="rfq-table">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                {["RFQ ID", "Material", "Sent to", "Delivery", "Quotes", "Due", "Status"].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {rfqs.map((r) => (
                  <TableRow key={r.id} onClick={() => setSel(r.id)} data-testid={`row-rfq-${r.rfq_no}`} className={`row-hover cursor-pointer ${sel === r.id ? "bg-sky-50 hover:bg-sky-50" : ""}`}>
                    <TableCell className="font-mono text-xs font-medium">{r.rfq_no}</TableCell>
                    <TableCell className="text-sm">{r.material}</TableCell>
                    <TableCell className="text-sm">{r.vendors.length} vendors</TableCell>
                    <TableCell><StatusBadge value={deliverySummary(r.vendors)} /></TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{r.vendors.filter((v) => v.quote_received).length} of {r.vendors.length}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{fmtDate(r.due_date)}</TableCell>
                    <TableCell><StatusBadge value={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Panel>
        {rfq && (
          <Panel className="min-w-0 lg:col-span-5" title={`Vendor delivery & read status · ${rfq.rfq_no}`} testId="vendor-status-panel"
            right={editable && <Button size="sm" variant="ghost" onClick={close} data-testid="btn-close-rfq"><Lock className="mr-1 h-3.5 w-3.5" />Close</Button>}>
            <div className="border-b border-slate-200 p-4">
              <DetailGrid testId="rfq-details" items={[["Material", rfq.material], ["Quantity", rfq.quantity], ["Location", rfq.delivery_location], ["Delivery by", rfq.delivery_date && fmtDate(rfq.delivery_date)], ["Payment terms", rfq.payment_terms], ["Contact", rfq.contact_person], ["Specification", rfq.specification], ["Terms", rfq.terms]]} />
            </div>
            <ul className="divide-y divide-slate-100">
              {rfq.vendors.map((v) => <VendorRow key={v.vendor_id} v={v} rfq={rfq} editable={editable} onUpdated={replace} l1={l1 != null && v.quote_amount === l1} />)}
            </ul>
            {rfq.po_ref && <p className="border-t border-slate-200 px-4 py-3 text-sm text-emerald-700" data-testid="rfq-po-ref">Purchase Order {rfq.po_ref} created from L1 quote</p>}
            {editable && (
              <div className="space-y-2 border-t border-slate-200 p-4">
                <Button className="w-full" onClick={remind} disabled={busy} data-testid="btn-send-reminder"><BellRing className="mr-1.5 h-4 w-4" />{busy ? "Sending..." : "Send reminder to non-responders"}</Button>
                {quotes.length > 0 && <Button variant="outline" className="w-full" onClick={() => setPoFor(rfq)} data-testid="btn-create-po"><FilePlus2 className="mr-1.5 h-4 w-4" />Create PO from L1 quote</Button>}
                {rfq.reminders.length > 0 && <p className="mt-2 text-xs text-slate-500" data-testid="last-reminder">Last reminder: {fmtDate(rfq.reminders.at(-1).at)} by {rfq.reminders.at(-1).by}</p>}
              </div>
            )}
          </Panel>
        )}
      </div>
      <NewRFQDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(r) => { setRfqs((a) => [r, ...a]); setSel(r.id); }} />
      {poFor && <CreatePODialog rfq={poFor} onClose={() => setPoFor(null)} onDone={replace} />}
    </div>
  );
}
